import DoneAllIcon from '@mui/icons-material/DoneAll';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * What the Review Lot & NDC dialog shows when the grid has no rows to draw.
 *
 * The print bar already carries the counts, so this is not a second copy of them — it is the
 * answer to the question the blank space provoked. Without it, a batch with nothing to list renders
 * several hundred pixels of nothing above the print bar, which reads as a screen that failed to
 * load rather than one with nothing to say.
 *
 * Two variants, because "no rows" has two very different causes and only one of them is good news:
 *
 *   ALL_CLEAR   every item was checked and none needs a decision. The review did its job.
 *   NOT_REVIEWED nothing was checked at all — the items are out of the queue or outside the
 *                user's facilities. They still print, but no lot on them has been verified, so
 *                this must not congratulate the user.
 *
 * Both end by pointing at the print bar, since the batch is printable either way, and both offer
 * the escape hatch: if a row was expected here, the data is wrong somewhere this screen cannot fix.
 */
export const REVIEW_EMPTY = { ALL_CLEAR: 'ALL_CLEAR', NOT_REVIEWED: 'NOT_REVIEWED' };

const COPY = {
  [REVIEW_EMPTY.ALL_CLEAR]: {
    Icon: DoneAllIcon,
    color: 'success.main',
    title: 'Nothing to change',
    body: 'Every selected item already has a usable NDC and lot, so none of them needs a decision here.',
  },
  [REVIEW_EMPTY.NOT_REVIEWED]: {
    Icon: SearchOffIcon,
    color: 'text.disabled',
    title: 'Nothing could be reviewed',
    body: 'None of the selected items could be checked — they may have left the queue, or sit outside your facilities.',
  },
};

const BulkLotNdcReviewEmptyState = ({ variant }) => {
  const { Icon, color, title, body } = COPY[variant] ?? COPY[REVIEW_EMPTY.ALL_CLEAR];

  return (
    <Box
      // Centred in whatever height is left between the header and the print bar, rather than
      // pinned under the header: the point of the message is to occupy the emptiness, not to sit
      // at the top of it. minHeight keeps it off the header on a short dialog.
      sx={{
        flex: 1,
        minHeight: 200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 3,
        py: 4,
      }}
    >
      <Icon sx={{ fontSize: 56, color, mb: 1.5 }} />

      <Typography variant='h6' gutterBottom>
        {title}
      </Typography>

      <Typography variant='body2' color='text.secondary' sx={{ maxWidth: 520 }}>
        {body}
      </Typography>

      <Typography variant='body2' color='text.secondary' sx={{ maxWidth: 520, mt: 1.5 }}>
        Download or print below as usual. If you expected something to review here, contact an
        admin.
      </Typography>
    </Box>
  );
};

BulkLotNdcReviewEmptyState.propTypes = {
  variant: PropTypes.oneOf(Object.values(REVIEW_EMPTY)).isRequired,
};

export default BulkLotNdcReviewEmptyState;
